'use client'

import { useMemo, useState, type ReactNode } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import { SectionCard } from '@/components/ui/section-card'
import { CardContent } from '@/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'

export type PreparedFaq = {
  id: string
  question: string
  /** Lower-cased concatenation of question + plain-text answer, precomputed on server. */
  searchText: string
  /** Pre-rendered answer JSX (RichText) produced on the server. */
  answer: ReactNode
}

export type PreparedFaqGroup = {
  categoryId: string
  categoryName: string
  faqs: PreparedFaq[]
}

type VolunteerFaqsLexicalClientProps = {
  groups: PreparedFaqGroup[]
}

export function VolunteerFaqsLexicalClient({ groups }: VolunteerFaqsLexicalClientProps) {
  const [query, setQuery] = useState('')

  const normalizedQuery = query.trim().toLowerCase()
  const hasQuery = normalizedQuery.length > 0

  const filteredGroups = useMemo(() => {
    if (!hasQuery) return groups
    return groups
      .map((group) => ({
        ...group,
        faqs: group.faqs.filter((faq) => faq.searchText.includes(normalizedQuery)),
      }))
      .filter((group) => group.faqs.length > 0)
  }, [groups, hasQuery, normalizedQuery])

  if (!groups.length) return null

  return (
    <section className="flex w-full flex-col gap-theme-md md:gap-theme-lg">
      <div className="relative w-full">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search FAQs..."
          aria-label="Search volunteer FAQs"
          className="pl-9"
        />
      </div>

      {filteredGroups.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No FAQs match &ldquo;{query}&rdquo;.
        </p>
      ) : (
        filteredGroups.map((group) => (
          <VolunteerFaqsCategoryCard key={group.categoryId} group={group} hasQuery={hasQuery} />
        ))
      )}
    </section>
  )
}

function VolunteerFaqsCategoryCard({
  group,
  hasQuery,
}: {
  group: PreparedFaqGroup
  hasQuery: boolean
}) {
  return (
    <SectionCard
      key={`${group.categoryId}-${hasQuery ? 'search' : 'default'}`}
      defaultOpen
      title={group.categoryName}
      bareContent
    >
      <CardContent className="divide-y divide-black p-0">
        {group.faqs.map((faq) => (
          <VolunteerFaqRow
            key={`${faq.id}-${hasQuery ? 'search' : 'default'}`}
            faq={faq}
            defaultOpen={hasQuery}
          />
        ))}
      </CardContent>
    </SectionCard>
  )
}

function VolunteerFaqRow({
  faq,
  defaultOpen,
}: {
  faq: PreparedFaq
  defaultOpen: boolean
}) {
  return (
    <Collapsible className="group" defaultOpen={defaultOpen}>
      <CollapsibleTrigger asChild>
        <div className="flex w-full cursor-pointer flex-row items-center justify-between gap-theme-md bg-white p-4 py-3 font-bold transition-colors hover:bg-neutral-100 group-data-[state=open]:border-b group-data-[state=open]:border-black group-data-[state=open]:bg-neutral-100">
          <span className="min-w-0 flex-1 text-left">{faq.question}</span>
          <ChevronDown
            aria-hidden="true"
            className="shrink-0 text-muted-foreground transition-transform duration-200 -rotate-90 group-data-[state=open]:rotate-0"
          />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="bg-white px-4 pb-4 pt-4 text-sm">{faq.answer}</div>
      </CollapsibleContent>
    </Collapsible>
  )
}
